import { useState, useEffect, useRef } from 'react';
import { useSession } from '../contexts/SessionContext';
import { useCalendar } from '../contexts/CalendarContext';
import { getSessionCustomers } from '../utils/api';
import {
  getContactFields,
  getExtraFields,
  getPossessionFields,
  fieldStateKey,
  fieldInputType,
  fieldLabel,
  isFieldRequired,
} from '../utils/dataFields';
import PossessionSection from './possession/PossessionSection';
import FieldLabel, { RequiredMark } from './FieldLabel';

// Format phone number for display as (xxx) xxx-xxxx
const formatForDisplay = (value) => {
  if (!value) return '';

  // Extract just the digits
  let digits = value.replace(/[^\d]/g, '');

  // If it starts with 1 and is 11 digits, remove the 1
  if (digits.startsWith('1') && digits.length === 11) {
    digits = digits.substring(1);
  }

  // Limit to 10 digits
  digits = digits.slice(0, 10);

  const length = digits.length;
  if (length === 0) return '';
  if (length < 4) return digits;
  if (length < 7) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  }
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

const PersonalInfoForm = () => {
  const {
    personalInfo,
    setPersonalInfo,
    phoneVerified,
    sessionId,
    selectedCustomerId,
    setSelectedCustomerId,
    setSelectedVehicleId,
    setVehicleCustomerRefId,
    setVehiclePossessionId,
  } = useSession();
  const { validatePhone, fields } = useCalendar();
  const isPhoneVerified = validatePhone && phoneVerified; // Phone is verified when validatePhone is true AND phoneVerified is true

  // Data fields configured for this calendar; each falls back to a sensible default.
  const extraFields = getExtraFields(fields);
  const contactFields = getContactFields(fields);
  // CIN-6871: show the legend only when this form actually has a required field.
  // Reads the same three field sources as the Next-button gate (Scheduler.js isStepValid).
  const hasRequiredField = [...contactFields, ...getPossessionFields(fields), ...extraFields].some(isFieldRequired);
  const [displayPhone, setDisplayPhone] = useState(() => {
    return personalInfo.phone ? formatForDisplay(personalInfo.phone) : '';
  });
  const [customerRecords, setCustomerRecords] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const hasInitializedForm = useRef(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPersonalInfo((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (e) => {
    // Since phone is already verified, don't allow changes
    e.preventDefault();

    if (!validatePhone) {
      const { value } = e.target;
      const formatted = formatForDisplay(value);
      setDisplayPhone(formatted);
      // Store unformatted digits in context
      const digits = formatted.replace(/[^\d]/g, '');
      setPersonalInfo((prev) => ({ ...prev, phone: digits }));
    }
  };

  // Sync display phone when personalInfo changes
  useEffect(() => {
    setDisplayPhone(formatForDisplay(personalInfo.phone));
  }, [personalInfo.phone]);

  // Fetch customer records when phone is verified
  useEffect(() => {
    const fetchCustomerRecords = async () => {
      if (isPhoneVerified && sessionId) {
        setLoadingCustomers(true);
        try {
          const response = await getSessionCustomers(sessionId);
          if (response && Array.isArray(response.customers) && response.customers.length > 0) {
            setCustomerRecords(response.customers);
          } else {
            setCustomerRecords([]);
          }
        } catch (error) {
          console.error('Error fetching customer records:', error);
          setCustomerRecords([]);
        } finally {
          setLoadingCustomers(false);
        }
      }
    };

    fetchCustomerRecords();
  }, [isPhoneVerified, sessionId]);

  // Auto-select a customer (from session or first) and prefill contact fields once
  // records load. Possession prefill is handled by the possession section.
  useEffect(() => {
    if (customerRecords.length > 0 && !loadingCustomers && !hasInitializedForm.current) {
      hasInitializedForm.current = true;
      const customer =
        (selectedCustomerId && customerRecords.find((c) => c.id === selectedCustomerId)) || customerRecords[0];
      if (customer) {
        if (!selectedCustomerId) setSelectedCustomerId(customer.id);
        setPersonalInfo((prev) => ({
          ...prev,
          first_name: customer.first_name || prev.first_name || '',
          last_name: customer.last_name || prev.last_name || '',
          email: customer.email || prev.email || '',
        }));
      }
    }
  }, [customerRecords, loadingCustomers, selectedCustomerId, setSelectedCustomerId, setPersonalInfo]);

  const handleCustomerSelection = (e) => {
    const customerId = e.target.value;
    setSelectedCustomerId(customerId);
    // Reset possession selection so the possession section re-prefills for the new customer.
    setSelectedVehicleId(null);
    setVehicleCustomerRefId(null);
    setVehiclePossessionId(null);

    if (customerId === 'new') {
      setPersonalInfo((prev) => ({ ...prev, first_name: '', last_name: '', email: '' }));
    } else {
      const customer = customerRecords.find((c) => c.id === customerId);
      if (customer) {
        setPersonalInfo((prev) => ({
          ...prev,
          first_name: customer.first_name || '',
          last_name: customer.last_name || '',
          email: customer.email || '',
        }));
      }
    }
  };

  return (
    <div className="step-content">
      <form className="personal-info-form">
        {hasRequiredField && (
          <p className="required-legend" data-testid="required-legend">
            <span className="required-mark" aria-hidden="true">
              *
            </span>{' '}
            Required
          </p>
        )}
        {isPhoneVerified && customerRecords.length > 0 && (
          <div className="form-section">
            <h3 className="section-title">Select Customer Record</h3>
            <div className="form-group">
              <label htmlFor="customerSelect">Choose an existing record or create new</label>
              <select
                id="customerSelect"
                value={selectedCustomerId || ''}
                onChange={handleCustomerSelection}
                disabled={loadingCustomers}
                style={{ width: '100%', padding: '10px', marginBottom: '20px' }}
              >
                <option value="">-- Select an option --</option>
                {customerRecords.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.first_name} {customer.last_name}
                  </option>
                ))}
                <option value="new">Create New Customer</option>
              </select>
            </div>
          </div>
        )}

        {(!selectedCustomerId || selectedCustomerId === 'new') && (
          <div className="form-section">
            <h3 className="section-title">{fields?.customer_label || 'Contact Information'}</h3>
            <div className="form-row" style={{ flexWrap: 'wrap' }}>
              {contactFields.map((field) => {
                const key = fieldStateKey(field);
                const isPhone = field.id === 'phone';
                const flex = field.type === 'textarea' ? '1 1 100%' : '1 1 calc(50% - 8px)';
                return (
                  <div className="form-group" key={field.id} style={{ flex }}>
                    {field.type !== 'boolean' && <FieldLabel htmlFor={key} field={field} />}
                    {field.type === 'boolean' ? (
                      <label className="field-checkbox" htmlFor={key}>
                        <input
                          type="checkbox"
                          id={key}
                          name={key}
                          checked={!!personalInfo[key]}
                          onChange={(e) => setPersonalInfo((prev) => ({ ...prev, [key]: e.target.checked }))}
                        />
                        <span className="field-checkbox-box" />
                        <span>{fieldLabel(field)}</span>
                        {/* CIN-6871: the label WRAPS the checkbox here, so the mark goes inside it. */}
                        {isFieldRequired(field) && <RequiredMark htmlFor={key} />}
                      </label>
                    ) : isPhone ? (
                      <>
                        <input
                          type="tel"
                          id={key}
                          name={key}
                          value={displayPhone}
                          onChange={handlePhoneChange}
                          placeholder={field.placeholder || ''}
                          disabled={isPhoneVerified}
                          required={!!field.required}
                        />
                        {isPhoneVerified && <span className="verified-badge">✓ Verified</span>}
                      </>
                    ) : field.type === 'textarea' ? (
                      <textarea
                        id={key}
                        name={key}
                        value={personalInfo[key] || ''}
                        onChange={handleInputChange}
                        placeholder={field.placeholder || ''}
                        required={!!field.required}
                        rows={4}
                        style={{ width: '100%', padding: '10px', resize: 'vertical', boxSizing: 'border-box' }}
                      />
                    ) : field.type === 'select' ? (
                      <select
                        id={key}
                        name={key}
                        value={personalInfo[key] || ''}
                        onChange={handleInputChange}
                        required={!!field.required}
                      >
                        <option value="">Select...</option>
                        {(field.options || []).map((opt, i) => (
                          <option key={i} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={fieldInputType(field)}
                        id={key}
                        name={key}
                        value={personalInfo[key] || ''}
                        onChange={handleInputChange}
                        placeholder={field.placeholder || ''}
                        required={!!field.required}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <PossessionSection customerRecords={customerRecords} />

        <div className="form-section">
          <h3 className="section-title">{fields?.extra_label || 'Additional Comments'}</h3>
          <div className="form-row" style={{ flexWrap: 'wrap' }}>
            {extraFields.map((field) => {
              // Field ids are `entity_data__<key>`; the value maps to that entity_data key.
              const key = fieldStateKey(field);
              const value = personalInfo[key] || '';
              const inputType = fieldInputType(field);
              // Text areas span the full row; text / select / other inputs are half width.
              const fieldWidth = field.type === 'textarea' ? '1 1 100%' : '1 1 calc(50% - 8px)';
              return (
                <div className="form-group" key={field.id} style={{ flex: fieldWidth }}>
                  {field.type !== 'boolean' && <FieldLabel htmlFor={key} field={field} />}
                  {field.type === 'boolean' ? (
                    <label className="field-checkbox" htmlFor={key}>
                      <input
                        type="checkbox"
                        id={key}
                        name={key}
                        checked={!!personalInfo[key]}
                        onChange={(e) => setPersonalInfo((prev) => ({ ...prev, [key]: e.target.checked }))}
                      />
                      <span className="field-checkbox-box" />
                      <span>{fieldLabel(field)}</span>
                      {/* CIN-6871: the label WRAPS the checkbox here, so the mark goes inside it. */}
                      {isFieldRequired(field) && <RequiredMark htmlFor={key} />}
                    </label>
                  ) : field.type === 'textarea' ? (
                    <textarea
                      id={key}
                      name={key}
                      value={value}
                      onChange={handleInputChange}
                      placeholder={field.placeholder || ''}
                      required={!!field.required}
                      rows={4}
                      style={{ width: '100%', padding: '10px', resize: 'vertical', boxSizing: 'border-box' }}
                    />
                  ) : field.type === 'select' ? (
                    <select id={key} name={key} value={value} onChange={handleInputChange} required={!!field.required}>
                      <option value="">Select...</option>
                      {(field.options || []).map((opt, i) => (
                        <option key={i} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      id={key}
                      name={key}
                      type={inputType}
                      value={value}
                      onChange={handleInputChange}
                      placeholder={field.placeholder || ''}
                      required={!!field.required}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </form>
    </div>
  );
};

export default PersonalInfoForm;
